import { observer } from 'mobx-react-lite'
import Stack from '@mui/material/Stack'
import CreateClientContactAutocomplete from 'features/clients/create/contact/CreateClientContactAutocomplete'
import { themeConfig } from '@gap/shared/theme'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import AvatarWithName from 'reusable/mui/avatar-with-name/AvatarWithName'
import Button from '@mui/material/Button'
import SkeletonLoader from 'reusable/mui/skeleton/SkeletonLoader'
import { useShareDocumentDialogStore } from './ShareDocumentDialogStore'
import GroupIcon from '@mui/icons-material/Group'
import { pluralise } from '@gap/utils/language-utils'
import Avatar from '@mui/material/Avatar'
import GapAlert from 'reusable/mui/alert/GapAlert'
import AdvisorAccessAvatars from './AdvisorAccessAvatars'
import AdvisorAccessPopover from './AdvisorAccessPopover'

export default observer(function ShareDocumentDialogInvite() {
   const shareDocumentDialogStore = useShareDocumentDialogStore()

   return <Stack sx={{ gap: 2 }}>
      {shareDocumentDialogStore.infoBoxText && <GapAlert severity="info" variant="standard" sx={{ mt: 2 }}>
         {shareDocumentDialogStore.infoBoxText}
      </GapAlert>}

      {
         shareDocumentDialogStore.formHandler.selectedContactsField.bindInput(ip => <CreateClientContactAutocomplete
            {...ip}
            id='email-recipient'
            label='Select a contact or create one'
            clientUsers={shareDocumentDialogStore.selectableClientUsers}
            advisorClientId={shareDocumentDialogStore.sp.client.advisorClientId}
            onChange={shareDocumentDialogStore.onContactIdsChange}
            dontFetchContacts
         />)
      }

      <Typography variant="overline" sx={{ color: themeConfig.primary[900] }}>Who has access</Typography>

      <AdvisorAccess />

      {
         shareDocumentDialogStore.whoHasAccessList.map((user, index) => <Box key={user ? user.email : 'user-still-loading' + index}>
            <SkeletonLoader loading={!user}>
               {user && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', ':hover .hover-button': { opacity: 1 } }}>
                  <Box
                     sx={{
                        flexGrow: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        mr: 2
                     }}
                  >
                     <AvatarWithName name={user.name} typographySx={{ fontSize: '20px' }} />
                     <Box sx={{ ml: 1 }}>
                        <Typography variant="body1" sx={{ color: themeConfig.primary[900] }}>
                           {user.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>
                           {user.email}
                        </Typography>
                     </Box>
                  </Box>
                  <Box sx={{ ml: 'auto' }}>
                     {(user.isPrimaryContact && shareDocumentDialogStore.primaryUserHasAccessByDefault) && <Typography variant="caption" sx={{ color: themeConfig.primary[600] }}>Primary contact</Typography>}
                     {(!user.isPrimaryContact || !shareDocumentDialogStore.primaryUserHasAccessByDefault) && <Box sx={{ ml: 'auto', opacity: 0, transition: 'opacity 0.2s' }} className="hover-button">
                        <Button variant="text" color="primary" data-email={user.email} onClick={shareDocumentDialogStore.removeClientAccessButtonHandler}>Remove</Button>
                     </Box>}
                  </Box>
               </Box>}
            </SkeletonLoader>
         </Box>)
      }
   </Stack>
})

const AdvisorAccess = observer(function AdvisorAccess() {
   const shareDocumentDialogStore = useShareDocumentDialogStore()

   const summary = <Box sx={{ ml: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      <Typography variant="body1" sx={{ color: themeConfig.primary[900] }}>
         {shareDocumentDialogStore.advisorAccessLabel}
      </Typography>
      <SkeletonLoader loading={shareDocumentDialogStore.activeMemberCountLoader.isLoading}>
         <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>
            {shareDocumentDialogStore.activeMemberCount} {pluralise(shareDocumentDialogStore.activeMemberCount, 'person', 'people')}
         </Typography>
      </SkeletonLoader>
   </Box>

   if (shareDocumentDialogStore.clientIsPublicToAdvisorOrg) return <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Avatar sx={{ backgroundColor: themeConfig.primary[400] }}>
         <GroupIcon fontSize="small" />
      </Avatar>
      {summary}
   </Box>

   return <>
      <Box
         onClick={shareDocumentDialogStore.openAdvisorAccessPopover}
         sx={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            alignItems: 'center',
            maxWidth: '100%',
            px: 1,
            py: 0.75,
            ml: -1,
            borderRadius: '20px',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            ':hover': { backgroundColor: themeConfig.primary[100] },
         }}
      >
         <AdvisorAccessAvatars users={shareDocumentDialogStore.advisorAccessUsers} />
         {summary}
      </Box>

      <AdvisorAccessPopover />
   </>
})
